<template>
  <div
    class="fixed top-4 right-4 z-50 space-y-3 max-w-sm w-full pointer-events-none px-4"
    data-testid="notification-container"
    data-qa="notification-container"
    :data-state="notificationStore.notifications.length ? 'visible' : 'empty'"
    aria-live="polite"
  >
    <transition-group name="notification">
      <div
        v-for="notification in notificationStore.notifications"
        :key="notification.id"
        class="pointer-events-auto bg-white rounded-lg shadow-lg border-l-4 overflow-hidden transition-all"
        :class="notificationClasses(notification.type)"
        :data-testid="`notification-${notification.type}-${notification.id}`"
        :data-qa="`notification-${notification.type}-${notification.id}`"
        :data-state="notification.type"
        role="status"
      >
        <div class="p-4 flex items-start space-x-3">
          <!-- Icon -->
          <div class="flex-shrink-0">
            <Icon
              :name="iconProps(notification.type).name"
              :class="iconProps(notification.type).class"
              class="h-6 w-6"
            />
          </div>

          <!-- Content -->
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-hp-gray-900">
              {{ notification.message }}
            </p>
          </div>

          <!-- Dismiss Button -->
          <button
            v-if="notification.dismissible"
            type="button"
            aria-label="Dismiss notification"
            :data-testid="`dismiss-notification-${notification.id}`"
            :data-qa="`dismiss-notification-${notification.id}`"
            class="flex-shrink-0 text-hp-gray-400 hover:text-hp-gray-600 transition-colors"
            @click="notificationStore.dismiss(notification.id)"
          >
            <Icon name="x" class="h-5 w-5" />
          </button>
        </div>

        <!-- Progress Bar -->
        <div
          v-if="notification.duration && notification.duration > 0"
          class="h-1 bg-hp-gray-200 overflow-hidden"
        >
          <div
            class="h-full transition-all ease-linear origin-left"
            :class="progressBarClasses(notification.type)"
            :style="{
              width: '100%',
              transitionDuration: `${notification.duration}ms`,
              transitionProperty: 'transform',
              transform: animatedIds.has(notification.id) ? 'scaleX(0)' : 'scaleX(1)',
            }"
          />
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from 'vue';
import { useNotificationStore } from '@/core/notifications/useNotificationStore';
import type { NotificationType, Notification } from '@/core/notifications/types';
import { Icon } from '@/components/atoms';

defineOptions({ name: 'NotificationContainer' });

const notificationStore = useNotificationStore();
const animatedIds = ref<Set<string>>(new Set());

// Trigger animation after notification is added
watch(
  () => notificationStore.notifications,
  (newNotifications: Notification[]) => {
    newNotifications.forEach((notification: Notification) => {
      if (!animatedIds.value.has(notification.id)) {
        nextTick(() => {
          animatedIds.value.add(notification.id);
        });
      }
    });
  },
  { deep: true }
);

const iconProps = (type: NotificationType) => {
  const icons = {
    success: { name: 'check-circle', class: 'text-hp-green-600' },
    error: { name: 'x-circle', class: 'text-red-600' },
    warning: { name: 'alert-triangle', class: 'text-yellow-600' },
    info: { name: 'info', class: 'text-hp-blue-600' },
  } as const;

  return icons[type] || { name: 'info', class: 'text-hp-gray-600' };
};

const notificationClasses = (type: NotificationType) => {
  const classes = {
    success: 'border-hp-green-500',
    error: 'border-red-500',
    warning: 'border-yellow-500',
    info: 'border-hp-blue-500',
  } as const;

  return classes[type] || 'border-hp-gray-500';
};

const progressBarClasses = (type: NotificationType) => {
  switch (type) {
    case 'success':
      return 'bg-hp-green-500';
    case 'error':
      return 'bg-red-500';
    case 'warning':
      return 'bg-yellow-500';
    case 'info':
      return 'bg-hp-blue-500';
    default:
      return 'bg-hp-gray-500';
  }
};
</script>

<style scoped>
.notification-enter-active,
.notification-leave-active {
  transition: all 0.3s ease;
}

.notification-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.notification-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.notification-move {
  transition: transform 0.3s ease;
}
</style>
